import { ref, h, onUnmounted, inject, watch, computed } from 'vue'
import CursorPaginationControls from '@/Components/DataGrid/Controls/CursorPaginationControls.vue'
import isEqual from 'lodash/isEqual'
import cloneDeep from 'lodash/cloneDeep'
import { useGridState } from '@/Components/DataGrid/Composables/grid-state'
import { getHeadersWithToken } from '@/utils/http'
import { PAGE_URL_PARAMS } from '@/utils/symbols'
import { usePoll } from '@/Composables/poll'

const DIRECTIONS = {
  AFTER: 'after',
  BEFORE: 'before'
}

const PAGE_SIZE = 25
const POLL_INTERVAL = 15000

function initStaleDataDetection({ gridApi, anchorCursor, nextCursor, endpoint, filters }) {
  const { context } = useGridState()

  context.needsRefresh = false

  context.refresh = () => {
    anchorCursor.value = null
    nextCursor.value = null
    gridApi.value.refreshServerSide({ purge: true })
  }

  const checkForStaleData = async () => {
    const [hasRows, row] = await longPoll()

    if (hasRows) {
      context.needsRefresh = true
      context.seenRowId = row.id
    }
  }

  const staleDataPoll = usePoll(checkForStaleData, { interval: POLL_INTERVAL })

  watch(
    () => context.needsRefresh,
    (needsRefresh) => {
      if (needsRefresh) {
        staleDataPoll.stop()
      }
    }
  )

  watch(anchorCursor, (updatedAnchorCursor) => {
    if (updatedAnchorCursor && !context.needsRefresh) {
      staleDataPoll.start()
    }
  })

  onUnmounted(() => {
    context.needsRefresh = false
  })

  const longPoll = async () => {
    try {
      const request = { before: anchorCursor.value, size: 1 }
      const payload = JSON.stringify({ ...request, salvePreFilter: { ...filters } })

      const response = await fetch(endpoint, {
        headers: getHeadersWithToken(),
        method: 'post',
        body: payload
      })
      const data = await response.json()
      return [data?.rows.length > 0, data.rows?.[0]]
    } catch (e) {
      console.error(e)
      return [false, null]
    }
  }
}

export const useCursorPagination = ({ gridApi, endpoint, filters, initialCursor = null }) => {
  const { context } = useGridState()
  const urlParams = inject(PAGE_URL_PARAMS)

  const direction = ref(DIRECTIONS.AFTER)

  const anchorCursor = ref(null)
  const prevCursor = ref(null)
  const nextCursor = ref(initialCursor)

  const hasNextPage = computed(() => nextCursor.value !== null)

  watch(prevCursor, (newVal, oldVal) => {
    if (newVal && newVal !== oldVal) {
      urlParams.cursor = String(newVal + 1)
    }
  })

  // We only use long polling if the user loaded the first page.
  // Future improvement: use long polling for all pages with timestamp.
  if (initialCursor === null) {
    initStaleDataDetection({ gridApi, anchorCursor, nextCursor, endpoint, filters })
  }

  const onNext = () => {
    direction.value = DIRECTIONS.AFTER
    gridApi.value.refreshServerSide({ purge: true })
  }
  const onPrev = () => {
    direction.value = DIRECTIONS.BEFORE
    gridApi.value.refreshServerSide({ purge: true })
  }

  let prevModelSnapshot

  const getRowsPreHandler = (params) => {
    // Remove the default pagination values from the request.
    delete params.request.startRow
    delete params.request.endRow
    params.request.size = PAGE_SIZE

    // If the request (without the cursor values) has changed in any way, hard reset.
    // i.e. treat the request as a request for "page 1" and re-capture the initial cursor.
    const modelSnapshot = cloneDeep({ ...params.request, salvePreFilter: { ...filters } })

    if (prevModelSnapshot && !isEqual(prevModelSnapshot, modelSnapshot)) {
      nextCursor.value = null
      anchorCursor.value = null
      lastResponseSnapshot = null
    }

    prevModelSnapshot = modelSnapshot

    // Add the custom cursor values to the request.
    if (direction.value === DIRECTIONS.AFTER) {
      if (nextCursor.value) {
        params.request.after = nextCursor.value
      }
    } else {
      params.request.before = prevCursor.value
    }

    return params
  }

  let lastResponseSnapshot = null

  const getRowsPostHandler = (data) => {
    let dataOverride = null

    // If no rows, return the last response snapshot.
    if (data.rows.length === 0) {
      dataOverride = { ...lastResponseSnapshot }
    }
    // If navigating backwards and there are less than PAGE_SIZE rows,
    // fill out the page with the last response snapshot.
    else if (direction.value === DIRECTIONS.BEFORE && data.rows.length < PAGE_SIZE) {
      const remainingCount = PAGE_SIZE - data.rows.length
      const fillRows = lastResponseSnapshot.rows.slice(0, remainingCount)
      dataOverride = {
        ...data,
        rows: [...data.rows, ...fillRows]
      }
      dataOverride.start = dataOverride.rows[0].id
      dataOverride.end = dataOverride.rows[dataOverride.rows.length - 1].id
    }

    if (dataOverride) {
      data = dataOverride
      anchorCursor.value = null
    }

    // If newer data is available, check if it's in the current page that was just fetched.
    // If it is, we can hide the refresh indicator.
    if (context.needsRefresh && context.seenRowId) {
      if (data.rows.find((row) => row.id === context.seenRowId)) {
        context.seenRowId = null
        context.needsRefresh = false
        // This allows init cursor to re-initialize correctly, so that long polling can resume.
        anchorCursor.value = null
      }
    }

    prevCursor.value = data.start
    nextCursor.value = data.end

    if (!anchorCursor.value) {
      anchorCursor.value = data.start
    }

    lastResponseSnapshot = data

    return data
  }

  function createCursorPaginationControls() {
    return {
      render() {
        return h(CursorPaginationControls, {
          onNext,
          onPrev,
          hasNextPage: hasNextPage.value,
          hasPrevPage: true,
          params: this.params
        })
      },
      props: ['params']
    }
  }

  const gridOptionsSetup = () => {
    return {
      pagination: false,
      statusBar: {
        statusPanels: [
          {
            statusPanel: createCursorPaginationControls()
          }
        ]
      }
    }
  }

  return {
    cursorPagination: {
      getRowsPreHandler,
      getRowsPostHandler,
      gridOptionsSetup
    }
  }
}
